
import type { Mat4 } from "./view";
import { identity } from "./view";
import type { WorldBounds } from "./window";
import { clamp } from "../clamp";
import { effectBasis, type PlacedEffect } from "./placedeffects";

/** One sub-mesh: a slice of the shared vertex/index blob, plus the texture it
 *  samples and the bounding box `footprintRadiusMm` culls with. Mirrors
 *  `meshes/index.json`'s own shape exactly -- this is that file's schema, not
 *  a redesign of it. */
export interface MeshEntry {
  readonly texture: string;
  readonly vertexOffset: number;
  readonly vertexCount: number;
  readonly indexOffset: number;
  readonly indexCount: number;
  readonly indexType: "uint16" | "uint32";
  readonly bboxMin: readonly [number, number, number];
  readonly bboxMax: readonly [number, number, number];
}

export interface ModelEntry {
  readonly meshes: readonly MeshEntry[];
  /** The model's `_lod_01` to `_lod_03` meshes, nearest first, when the pack
   *  holds them (`Area.cpp:629-645`). */
  readonly lods?: readonly { readonly meshes: readonly MeshEntry[] }[];
}

/** `LOD_APPLY_MIN` (`LODController.cpp:11`): within this of the camera's
 *  target a model keeps its full mesh. 500 units. */
const LOD_APPLY_MIN_MM = 5_000;
/** `LODHEIGHT_ACTOR` (`LODController.cpp:4`): a model whose bound box is
 *  shorter than this on every axis takes the actor schedule. 500 units. */
const LOD_HEIGHT_ACTOR_MM = 5_000;
/** `LODDISTANCE_ACTOR` and `LODDISTANCE_BUILDING` (`LODController.cpp:5-6`):
 *  5,000 and 25,000 units. */
const LOD_DISTANCE_ACTOR_MM = 50_000;
const LOD_DISTANCE_BUILDING_MM = 250_000;
/** The actor schedule's own rungs (`LODController.cpp:484-495`): 500, 1,500
 *  and 2,500 units. */
const LOD_ACTOR_RUNGS_MM = [5_000, 15_000, 25_000] as const;

/** The meshes `CGrannyLODController::UpdateLODLevel` draws a placed model with
 *  (`LODController.cpp:453-520`), from its distance to the camera's target on
 *  the ground plane and to the eye in space (`ThingInstance.cpp:733-744`).
 *  The chain is the model's own meshes followed by its `lods`; level 0 is the
 *  last of them (`AddModel` pushes each one to the front, `:288`). A model
 *  whose base bound box reaches `LODHEIGHT_ACTOR` spreads its levels evenly
 *  over `LODDISTANCE_BUILDING`; a smaller one takes the actor rungs, where the
 *  level past the chain's end is its lowest detail. */
export function lodMeshes(
  entry: ModelEntry,
  fromTargetMm: number,
  fromEyeMm: number,
): readonly MeshEntry[] {
  const lods = entry.lods ?? [];
  if (lods.length === 0 || fromTargetMm <= LOD_APPLY_MIN_MM) return entry.meshes;
  // Nearest first: index 0 the full mesh, then each lower level.
  const chain = [entry.meshes, ...lods.map((lod) => lod.meshes)];
  const count = chain.length;
  let size = 0;
  for (const mesh of entry.meshes) {
    for (let axis = 0; axis < 3; axis += 1) {
      size = Math.max(size, Math.abs(mesh.bboxMax[axis] - mesh.bboxMin[axis]));
    }
  }
  // `SetLODLimits` from the base's bound box (`LODController.cpp:270-280`).
  const lodDistanceMm = size < LOD_HEIGHT_ACTOR_MM ? LOD_DISTANCE_ACTOR_MM : LOD_DISTANCE_BUILDING_MM;
  // `m_bLODLevel` counts from the lowest detail up; `chain` counts down.
  const factor = Math.min(Math.max(lodDistanceMm - fromEyeMm, 0), lodDistanceMm);
  let level = Math.floor(factor / (lodDistanceMm / count));
  if (lodDistanceMm <= LOD_DISTANCE_ACTOR_MM) {
    let rung = LOD_ACTOR_RUNGS_MM.findIndex((limit) => fromEyeMm < limit);
    if (rung === -1) rung = LOD_ACTOR_RUNGS_MM.length;
    level = Math.max(0, count - Math.min(rung, count) - 1);
  }
  level = Math.min(count - 1, level);
  return chain[count - 1 - level];
}

/** `meshes/index.json`, in full. */
export interface MeshManifest {
  readonly vertexStride: number;
  readonly models: Readonly<Record<string, ModelEntry>>;
}

export interface TextureEntry {
  readonly file: string;
  readonly width: number;
  readonly height: number;
}

/** `meshtex/index.json`, in full. */
export interface TextureManifest {
  readonly textures: Readonly<Record<string, TextureEntry>>;
}

export interface PlacementPosition {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
}

export interface Placement {
  readonly model: string;
  readonly position: PlacementPosition;
  /** The roll, about the up axis. */
  readonly rotationDeg: number;
  /** Absent, both, for an object its area row turns only by the roll. */
  readonly yawDeg?: number;
  readonly pitchDeg?: number;
}

export interface TreePlacement extends Placement {
  readonly size: number | null;
}

/** `placements.json`'s `buildings`, `trees` and `effects` arrays and the
 *  map's patch count. `unresolved` is not here: those placements resolved to
 *  no property record at all, not even a name, so there is nothing to draw
 *  for them. */
export interface PlacementsManifest {
  readonly buildings: readonly Placement[];
  readonly trees: readonly TreePlacement[];
  readonly effects?: readonly PlacedEffect[];
  readonly meta?: { readonly mapSize: { readonly patchesX: number; readonly patchesY: number } };
}

/** A placement's world matrix, column-major: `CGraphicObjectInstance::SetRotation`'s
 *  `D3DXMatrixRotationYawPitchRoll` (`GrpObjectInstance.cpp:172-181`) under
 *  this tree's y mirror, then the position (`:98-105`). Column j is where local
 *  axis j lands, so the determinant is -1 on purpose. */
export function modelMatrix(
  position: PlacementPosition,
  rotationDeg: number,
  yawDeg = 0,
  pitchDeg = 0,
  out: Mat4 = identity(),
): Mat4 {
  const basis = effectBasis(yawDeg, pitchDeg, rotationDeg);
  for (let axis = 0; axis < 3; axis += 1) {
    // `+ 0` folds a -0 the products leave into the 0 an untilted axis holds.
    out[axis * 4] = basis[axis * 3] + 0;
    out[axis * 4 + 1] = basis[axis * 3 + 1] + 0;
    out[axis * 4 + 2] = basis[axis * 3 + 2] + 0;
    out[axis * 4 + 3] = 0;
  }
  out[12] = position.xMm;
  out[13] = position.yMm;
  out[14] = position.zMm;
  out[15] = 1;
  return out;
}

export function footprintRadiusMm(
  bboxMin: readonly [number, number, number],
  bboxMax: readonly [number, number, number],
): number {
  const farX = Math.max(Math.abs(bboxMin[0]), Math.abs(bboxMax[0]));
  const farY = Math.max(Math.abs(bboxMin[1]), Math.abs(bboxMax[1]));
  return Math.hypot(farX, farY);
}

/** Does a circle on the ground plane touch a screen-space world rectangle? */
export function circleIntersectsBounds(
  centreXMm: number,
  centreYMm: number,
  radiusMm: number,
  bounds: WorldBounds,
): boolean {
  const nearestX = clamp(centreXMm, bounds.minXMm, bounds.maxXMm);
  const nearestY = clamp(centreYMm, bounds.minYMm, bounds.maxYMm);
  const dx = centreXMm - nearestX;
  const dy = centreYMm - nearestY;
  return dx * dx + dy * dy <= radiusMm * radiusMm;
}

/** A local point through a `modelMatrix`, which is rotation and translation
 *  only -- the same restriction `gl/props.ts`'s vertex shader relies on. */
function transformPoint(
  m: Mat4,
  p: readonly [number, number, number],
): [number, number, number] {
  return [
    m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12],
    m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13],
    m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14],
  ];
}

export function distanceSqToEye(
  position: PlacementPosition,
  eye: PlacementPosition,
): number {
  const dx = position.xMm - eye.xMm;
  const dy = position.yMm - eye.yMm;
  const dz = position.zMm - eye.zMm;
  return dx * dx + dy * dy + dz * dz;
}

/** A model's bounding sphere in its own local frame:
 *  `CGraphicThingInstance::BuildBoundingSphere`
 *  (`EterGrnLib/ThingInstance.cpp:89-98`) is the box midpoint, with a radius of
 *  half the box diagonal plus 50 units for whatever is attached to it. */
export function boundingSphere(meshes: readonly MeshEntry[]): {
  readonly centreMm: readonly [number, number, number];
  readonly radiusMm: number;
} {
  if (meshes.length === 0) return { centreMm: [0, 0, 0], radiusMm: 0 };
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (const mesh of meshes) {
    for (let axis = 0; axis < 3; axis += 1) {
      min[axis] = Math.min(min[axis], mesh.bboxMin[axis]);
      max[axis] = Math.max(max[axis], mesh.bboxMax[axis]);
    }
  }
  const diagonal = Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]);
  return {
    centreMm: [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2],
    // 50 legacy units, and this tree measures in millimetres at ten to the
    // unit (`camera.ts`'s own `CAMERA_MAX_DISTANCE_SHORT_MM` against
    // `CameraProcedure.cpp:11`).
    radiusMm: diagonal * 0.5 + 500,
  };
}

/** One placement, resolved against the mesh manifest and ready to draw. */
export interface PropDrawItem {
  readonly model: Mat4;
  readonly meshes: readonly MeshEntry[];
  readonly distanceSq: number;
  /** Where this placement's bounding sphere sits in the world, for the
   *  blocker sweep. The local centre's x and y turn with the placement's own
   *  rotation; the radius does not. */
  readonly centreMm: readonly [number, number, number];
  readonly radiusMm: number;
  /** Always false here: `buildVisibleProps` only ever sees buildings, and
   *  `GlTrees` offers its foliage to the same sweep on its own. */
  readonly isTree: boolean;
}

export interface VisibleProps {
  readonly items: readonly PropDrawItem[];
  readonly missingModels: number;
}

export function buildVisibleProps(
  placements: readonly Placement[],
  models: MeshManifest,
  bounds: WorldBounds,
  eye: PlacementPosition,
  target: { readonly xMm: number; readonly yMm: number } = eye,
): VisibleProps {
  const items: PropDrawItem[] = [];
  let missingModels = 0;

  for (const placement of placements) {
    const entry = models.models[placement.model];
    if (entry === undefined) {
      missingModels++;
      continue;
    }

    const yawDeg = placement.yawDeg ?? 0;
    const pitchDeg = placement.pitchDeg ?? 0;
    // A roll leaves a box's distance from its up axis alone; a yaw or a pitch
    // tips its height into the ground plane, so that placement is culled on
    // the far corner in all three axes instead.
    const tilted = yawDeg !== 0 || pitchDeg !== 0;
    let radiusMm = 0;
    for (const mesh of entry.meshes) {
      radiusMm = Math.max(radiusMm, tilted
        ? Math.hypot(
          footprintRadiusMm(mesh.bboxMin, mesh.bboxMax),
          Math.max(Math.abs(mesh.bboxMin[2]), Math.abs(mesh.bboxMax[2])),
        )
        : footprintRadiusMm(mesh.bboxMin, mesh.bboxMax));
    }
    if (!circleIntersectsBounds(placement.position.xMm, placement.position.yMm, radiusMm, bounds)) {
      continue;
    }

    const sphere = boundingSphere(entry.meshes);
    const model = modelMatrix(placement.position, placement.rotationDeg, yawDeg, pitchDeg);
    const distanceSq = distanceSqToEye(placement.position, eye);
    const fromTargetMm = Math.hypot(placement.position.xMm - target.xMm, placement.position.yMm - target.yMm);
    items.push({
      model,
      meshes: lodMeshes(entry, fromTargetMm, Math.sqrt(distanceSq)),
      distanceSq,
      centreMm: transformPoint(model, sphere.centreMm),
      radiusMm: sphere.radiusMm,
      isTree: false,
    });
  }

  items.sort((a, b) => a.distanceSq - b.distanceSq);
  return { items, missingModels };
}

export function resolveTreeTexture(model: string, textures: TextureManifest): string | null {
  const lastDot = model.lastIndexOf(".");
  const stem = lastDot < 0 ? model : model.slice(0, lastDot);
  const candidate = `${stem}.dds`;
  return candidate in textures.textures ? candidate : null;
}

/** A tree billboard's width and height, in millimetres, base-of-trunk to top
 *  of canopy. */
export interface TreeQuadSize {
  readonly widthMm: number;
  readonly heightMm: number;
}

const BASE_TREE_WIDTH_MM = 11_264;
const BASE_TREE_HEIGHT_MM = 11_000;

/** `TreeVariance` -- a random per-instance size spread the original applies at
 *  spawn time -- is deliberately not reproduced: this renderer has no seed
 *  compatible with the original's, so any variance it invented would be a
 *  second, disagreeing kind of randomness rather than the original's own. */
export function treeQuadSizeMm(size: number | null): TreeQuadSize {
  const scale = size === null || size <= 0 ? 1 : size / 1000;
  return { widthMm: BASE_TREE_WIDTH_MM * scale, heightMm: BASE_TREE_HEIGHT_MM * scale };
}
